import React, { useEffect, useMemo } from 'react';
import { Switch, useHistory } from 'react-router';

import { AUTH_ROUTE, MY_BOOKINGS_ROUTE } from './routeConstants';
import { LOGGED_IN_ROUTES, NON_LOGGED_IN_ROUTES } from 'routes';
import RouteWithSubRoutes from 'components/routeWithSubRoutes';
import { useHelpCrunch } from 'context/helpCrunchContext';

const AppRoutes = ({ loading, isLoggedIn }) => {
  const { isHelpCrunchReady } = useHelpCrunch();
  const history = useHistory();

  const appRoutes = useMemo(() => {
    if (loading) {
      return [];
    }
    if (isLoggedIn) {
      return LOGGED_IN_ROUTES;
    }
    return NON_LOGGED_IN_ROUTES;
  }, [loading, isLoggedIn]);

  // Handle root path redirect synchronously to prevent flash
  const shouldRedirectFromRoot = useMemo(() => {
    return !loading && history?.location?.pathname === '/';
  }, [loading, history?.location?.pathname]);

  // All hooks must be called before any conditional returns
  useEffect(() => {
    // HelpCrunch setup
    if (isHelpCrunchReady) {
      window?.HelpCrunch('closeChat');
      window?.HelpCrunch('hideChatWidget');
      setTimeout(() => {
        window?.HelpCrunch('hideChatWidget');
      }, 100);
    }
  }, [isHelpCrunchReady]);

  // Handle delayed redirect for root path (after all hooks)
  useEffect(() => {
    if (shouldRedirectFromRoot) {
      // Add 10-second delay before auto-redirect
      const redirectTimer = setTimeout(() => {
        const urlParams = new URLSearchParams(history.location.search);
        const next = urlParams.get('next');

        if (isLoggedIn) {
          // If user just logged in and has a 'next' destination, go there
          if (next && next !== '/auth' && next !== '/auth/') {
            history.replace(next);
          } else {
            // Otherwise go to default logged-in page
            history.replace(MY_BOOKINGS_ROUTE);
          }
        } else {
          // Clear any stale next parameters when not logged in
          history.replace(AUTH_ROUTE);
        }
      }, 10000); // 10-second delay

      // Cleanup timer if component unmounts or dependencies change
      return () => clearTimeout(redirectTimer);
    }
  }, [shouldRedirectFromRoot, isLoggedIn, history]);

  // Show loading while determining authentication state
  if (loading) {
    return null;
  }

  return (
    <Switch>
      {appRoutes.map((route, i) => (
        <RouteWithSubRoutes key={i} {...route} />
      ))}
    </Switch>
  );
};

export default AppRoutes;
